<template>
    <div>
        <template v-if="isLoaded">
            <h2>{{ $t("manager.company.common.title") | capitalize-first }} #{{ id }}</h2>

            <hr>

            <form>
                <b-tabs pills card>
                    <b-tab :title="$t('manager.company.common.title') | capitalize-first" active>
                        <div class="form-group row">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.common.oscarId") | capitalize-first }}</label>
                            <div class="col-9 col-md-10">
                                <input type="text" class="form-control" v-model="oscarId">
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.common.name") | capitalize-first }}</label>
                            <div class="col-9 col-md-10">
                                <input type="text" class="form-control" v-model="name">
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.common.address1") | capitalize-first }}</label>
                            <div class="col-9 col-md-10">
                                <input type="text" class="form-control" v-model="address1">
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.common.address2") | capitalize-first }}</label>
                            <div class="col-9 col-md-10">
                                <input type="text" class="form-control" v-model="address2">
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.common.vat") | capitalize-first }}</label>
                            <div class="col-9 col-md-10">
                                <input type="text" class="form-control" v-model="vat">
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.common.email") | capitalize-first }}</label>
                            <div class="col-9 col-md-10">
                                <input type="email" class="form-control" v-model="email">
                            </div>
                        </div>
                    </b-tab>
                    <b-tab :title="$t('manager.company.theme.title') | capitalize-first">
                        <!-- ThemeEditor -->
                        <div class="form-group row">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.theme.textAndColors") | capitalize-first }}</label>
                            <div class="col-9 col-md-10">
                                <theme-editor :theme="theme" :images="images"></theme-editor>
                            </div>
                        </div>
                        <!-- logo -->
                        <div class="form-group row">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.theme.logo") | capitalize-first }}</label>
                            <div class="col-9 col-md-10">
                                <file-uploader :backendId="id" :for="'logo'" :filename="images.logo" resolution="" @input="images.logo = arguments[0]"></file-uploader>
                            </div>
                        </div>
                        <!-- banner -->
                        <div class="form-group row">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.theme.banner") | capitalize-first }}</label>
                            <div class="col-9 col-md-10">
                                <file-uploader :backendId="id" :for="'banner'" :filename="images.banner" resolution="1920x420" @input="images.banner = arguments[0]"></file-uploader>
                            </div>
                        </div>
                        <!-- product placeholder -->
                        <div class="form-group row">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.theme.product") | capitalize-first }}</label>
                            <div class="col-9 col-md-10">
                                <file-uploader :backendId="id" :for="'product'" :filename="images.product" resolution="" @input="images.product = arguments[0]"></file-uploader>
                            </div>
                        </div>
                    </b-tab>
                    <b-tab :title="$t('manager.company.sales.title') | capitalize-first">
                        <sales-list :backendId="id"></sales-list>
                    </b-tab>
                    <b-tab v-if="isAdmin" :title="$t('manager.company.billing.title') | capitalize-first">
                        <billing-list :backendId="id"></billing-list>
                    </b-tab>
                    <b-tab :title="$t('manager.company.settings.title') | capitalize-first">
                        <div class="form-group row w-100" v-for="(setting, index) in settings" :key="index">
                            <label class="col-3 col-md-2 col-form-label">{{ $t("manager.company.settings." + index) }}</label>
                            <div class="col-9 col-md-10">
                                <div class="form-check">
                                    <input type="checkbox" class="form-check-input" v-model="settings[index]">
                                </div>
                            </div>
                        </div>
                    </b-tab>
                </b-tabs>

                <button class="btn btn-default" @click.prevent="updateChanges">{{ $t("save") | capitalize-first }}</button>

                <div class="action-feedback">
                    <span v-if="saved" class="badge badge-success">
                        <font-awesome-icon icon="check-circle" />
                        {{ $t("manager.feedback.saved") }}
                    </span>
                    <span v-if="hasChanges" class="badge badge-warning">
                        <font-awesome-icon icon="exclamation-circle" />
                        {{ $t("manager.feedback.unsavedChanges") }}
                    </span>
                </div>

                <b-modal ref="confirmExitModal" hide-footer hide-header>
                    <div class="d-block text-center">
                        <p>{{ $t("confirmUnsavedChanges") }}</p>
                    </div>

                    <b-btn class="mt-3" variant="danger" @click="hideModal">{{ $t("cancel") | capitalize-first }}</b-btn>
                    <b-btn class="mt-3" style="float:right;" variant="primary" @click="navigateToTargetRoute">{{ $t("continue") | capitalize-first }}</b-btn>
                </b-modal>
            </form>
        </template>
        <template v-else>
            <div class="text-center">
                <font-awesome-icon icon="spinner" pulse />
            </div>
        </template>
    </div>
</template>

<script>
import SalesList from './SalesList.vue'
import BillingList from './BillingList.vue'
import ThemeEditor from './utilities/ThemeEditor.vue'
import FileUploader from './utilities/FileUploader.vue'
import axios from 'axios'

export default {
  name: 'Company',
  data () {
    return {
      isLoaded: false,
      id: 0,
      oscarId: null,
      name: null,
      address1: null,
      address2: null,
      vat: null,
      email: null,
      images: {
        logo: null,
        banner: null,
        product: null
      },
      theme: {},
      settings: {},
      hasChanges: false,
      saved: false,
      targetRoute: null
    }
  },
  components: {
    SalesList,
    BillingList,
    ThemeEditor,
    FileUploader
  },
  computed: {
    isAdmin () {
      return this.$store.getters.isAdmin
    }
  },
  methods: {
    updateChanges () {
      let me = this
      axios.post(me.$store.getters.urlPath + 'services/manager/saveBackendIdData.php', {
        id: me.id,
        oscarId: me.oscarId,
        name: me.name,
        address1: me.address1,
        address2: me.address2,
        vat: me.vat,
        email: me.email,
        images: me.images,
        theme: me.theme,
        settings: me.settings
      })
        .then((response) => {
          me.hasChanges = false
          me.saved = true
        })
        .catch((error) => {
          // eslint-disable-next-line
          console.log(error)
        })
    },
    showModal () {
      this.$refs.confirmExitModal.show()
    },
    hideModal () {
      this.$refs.confirmExitModal.hide()
    },
    navigateToTargetRoute () {
      this.hasChanges = false
      this.saved = false
      this.$router.push({ path: this.targetRoute.path })
    },
    loadCompany: function (id) {
      let me = this
      axios.get(me.$store.getters.urlPath + 'services/getBackendIdData.php?id=' + id, {
        headers: {
          'Cache-Control': 'no-cache'
        }
      })
        .then((response) => {
          me.id = response.data.id
          me.oscarId = response.data.oscarId
          me.name = response.data.name
          me.address1 = response.data.address1
          me.address2 = response.data.address2
          me.vat = response.data.vat
          me.email = response.data.email
          me.images = response.data.images
          me.theme = response.data.theme
          me.settings = response.data.settings
          me.hasChanges = false
          me.saved = false
          me.isLoaded = true
        })
        .catch((error) => {
          // eslint-disable-next-line
          console.log(error)
        })
    }
  },
  watch: {
    oscarId: function (val, oldVal) {
      this.hasChanges = val !== null && oldVal !== null
      this.saved = false
    },
    name: function (val, oldVal) {
      this.hasChanges = val !== null && oldVal !== null
      this.saved = false
    },
    address1: function (val, oldVal) {
      this.hasChanges = val !== null && oldVal !== null
      this.saved = false
    },
    address2: function (val, oldVal) {
      this.hasChanges = val !== null && oldVal !== null
      this.saved = false
    },
    vat: function (val, oldVal) {
      this.hasChanges = val !== null && oldVal !== null
      this.saved = false
    },
    email: function (val, oldVal) {
      this.hasChanges = val !== null && oldVal !== null
      this.saved = false
    },
    logo: function (val, oldVal) {
      this.hasChanges = val !== null && oldVal !== null
      this.saved = false
    }
  },
  created () {
    this.loadCompany(this.$route.params.id)
  },
  beforeRouteLeave (to, from, next) {
    if (!this.hasChanges) {
      next()
    } else {
      this.targetRoute = to
      this.showModal()
      next(false)
    }
  }
}
</script>

<style lang="scss">
#manager .tabs {
    .card-header {
        background-color: transparent;
        border: none;

        ul.nav.nav-pills {
            background-color: #222534;

            > li.nav-item {
                > a.nav-link {
                    color: #fff !important;
                    &.active {
                        background-color: #1a1c28;
                        color: #d9a800 !important;
                    }
                }
            }
        }
    }
}
</style>
